import { Component, computed, inject, signal } from '@angular/core'
import { RouterOutlet } from '@angular/router'
import { HeaderComponent } from '@features/header/header.component'
import { ThemeService } from '@services/theme.service'
import { NavigationComponent } from '@features/navigation/navigation.component'
import { IconButtonComponent } from '@components/iconButton/iconButton.component'
import { EnvironmentService } from './services/environment.service'
import { AuthenticationService } from './services/authentication/authentication.service'
import { AuthenticationComponent } from './features/reAuthentication/reAuthentication.component'
import { AuthenticationViewComponent } from './features/authenticationView/authenticationView.component'
import { LoadingService } from './services/loading.service'
import { DataRefreshService } from './services/dataRefresh.service'

@Component({
    selector: 'vco-toast',
    imports: [IconButtonComponent],
    template: `
        <dialog [open]="true" class="toast" [class.open]="open()">
            <div><ng-content></ng-content></div>
            <div>
                <vco-icon-button (click)="close()">close</vco-icon-button>
            </div>
        </dialog>
    `,
    styles: `
        .toast {
            bottom: -100vh;
            user-select: none;
            transition: all 0.3s ease-in-out;
            display: flex;
            justify-content: space-between;
            align-items: center;
            opacity: 0;
            position: fixed;
        }
        .toast.open {
            opacity: 1;
            bottom: 1rem;
        }
    `,
}) export class ToastComponent {
    readonly open = signal(true)

    close = () => {
        this.open.set(false)
    }
}

@Component({
    selector: 'vco-root',
    imports: [RouterOutlet, HeaderComponent, NavigationComponent, IconButtonComponent, AuthenticationComponent, AuthenticationViewComponent, ToastComponent],
    template: `
        <div class="layout"
            [class.layout-nav-closed]="navClosed()">

            <div class="sidebar-toggle"
                (click)="navClosed.set(!navClosed())">
                @if (isLoading()) {
                    <span class="material-icons-outlined spin">autorenew</span>
                }
                @if (environment()) { <span class="text-lightest p-2">({{environment()}})</span> }
                @if (!navClosed()) { Hide Sidebar &nbsp;}
                <vco-icon-button>
                    {{navClosed() ? 'chevron_right' : 'chevron_left'}}
                </vco-icon-button>
            </div>
            
            <div class="sidebar">
                @if (!navClosed()) {
                    <vco-navigation></vco-navigation>
                }
            </div>

            <div class="header">
                <vco-header></vco-header>
                <vco-authentication-view></vco-authentication-view>
            </div>

            <div class="content">
                <router-outlet></router-outlet>

            </div>

            <vco-authentication></vco-authentication>
            @if (longLoading()) {
                <vco-toast>Please wait while services start.</vco-toast>
            }
        </div>
    `,
    styles: [`
        .layout {
            display: grid;
            grid-template-columns: 30ch 1fr;
            grid-template-rows: auto 1fr;
            grid-template-areas: 
                "sidebar-toggle header"
                "sidebar content";
            height: 100vh;
        }

        .layout.layout-nav-closed {
            grid-template-columns: auto 1fr; /* Hide sidebar when nav is closed */
        }

        .sidebar {
            grid-area: sidebar;
            padding: 0.5rem;
            overflow-y: auto; /* Handle sidebar overflow */
        }

        .sidebar-toggle {
            display: flex;
            justify-content: end;
            align-items: center;
            color: var(--color-textLightest);
            padding: 1rem 0.5rem 0 .5rem;
            position: sticky;
            top: 0;
            cursor: pointer;
        }

        .header {
            grid-area: header;
            display: flex;
            align-items: end;
            padding: 0;
        }

        .content {
            grid-area: content;
            background: var(--color-background);
            overflow-y: auto; /* Handle content overflow */
            padding: .5rem;
            margin: 0 .5rem .5rem 0;
            display: flex;
        }
    `]
})
export class AppComponent {
    constructor(
        private _themeService: ThemeService,
        private _environmentService: EnvironmentService,
        private _authenticationService: AuthenticationService,
        private _loadingService: LoadingService,
        private _dataRefreshService: DataRefreshService,
    ) { }
    readonly environment = computed(() => !this._environmentService.isProduction() ? this._environmentService.getCurrentEnvironment() : undefined)

    readonly navClosed = signal(false)

    readonly isLoading = computed(() => this._loadingService.isLoading())

    readonly longLoading = computed(() => this._loadingService.isLongLoading())
}
